import React, { useState, useContext } from "react";
import styles from "./styles.scss";
import { useMutation } from "@apollo/client";
import { CREATE_USER_MUTATION } from "src/gql/mutations";
import AnalyticsService from "src/services/AnalyticsService";
import { getCxd } from "src/services/CellxpertService";
import ClaritasService from "src/services/ClaritasService";
import GAService from "src/services/GAService";
import { DEFAULT_SPORT_KEY, STATES } from "src/constants";
import { SubscriptionContext } from "src/services/SubscriptionService";
import { mdiCheckBold, mdiArrowRight } from "@mdi/js";
import Icon from "@mdi/react";
import Link from "next/link";
import { Input, Spinner } from "reactstrap";
import Dropdown from "../Dropdown";
import BetQLLogo from "src/components/BetQLLogo";
import { useLocalStorage } from "src/services/UtilService";

const validateInput = (email: string, selectedStateIndex: number) => {
  const trimmedEmail = email.trim();
  if (!trimmedEmail) {
    return "Email cannot be empty";
  }

  const regex = /\S+@\S+\.\S+/;
  if (trimmedEmail && !regex.test(trimmedEmail)) {
    return "Not a valid email address";
  }

  if (selectedStateIndex < 0) {
    return "Select your state";
  }
  return "";
};

// prettier-ignore
const EmailStateCapture = ({ ctaText = "SUBSCRIBE" }: { ctaText?: string }) => {
  const [createUser, { loading }] = useMutation(CREATE_USER_MUTATION);
  const { signedIn } = useContext(SubscriptionContext);
  const [selectedStateIndex, setStateIndex] = useState(0);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [emailCaptured, setEmailCaptured] = useLocalStorage<boolean>("emailInLocalStorage");
  const isVisible = !emailCaptured && !signedIn;

  const onSubmit = async () => {
    const validateError = validateInput(email, selectedStateIndex);

    if (validateError) {
      return setError(validateError);
    }

    try {
      setError("");
      const { data } = await createUser({
        variables: {
          cxd: getCxd(),
          email,
          emailOnly: true,
          state: STATES[selectedStateIndex].value,
        },
      });

      const _success = Boolean(data?.createUser?.token);

      if (!_success) {
        GAService.sendEvent({
          hitType: "event",
          eventCategory: "Email Capture - Fail",
          eventAction: window.location.href,
          eventLabel: "Email Capture - Submission Form",
        });
        AnalyticsService.track("Email Capture - Fail", {
          page: window.location.href,
          message: "No response data",
        });
        return;
      }

      GAService.sendEvent({
        hitType: "event",
        eventCategory: "Email Capture - Success",
        eventAction: window.location.href,
        eventLabel: "Email Capture - Submission Form",
      });
      AnalyticsService.track("Email Capture - Success", {
        page: window.location.href,
      });
      ClaritasService.trackEmailCapture();

      setEmailCaptured(true);
    } catch (err: any) {
      const message = err?.graphQLErrors?.[0]?.message;
      setError(message);

      if (message) {
        GAService.sendEvent({
          hitType: "event",
          eventCategory: "Email Capture - Fail",
          eventAction: window.location.href,
          eventLabel: "Email Capture - Submission Form",
        });
        AnalyticsService.track("Email Capture - Fail", {
          page: window.location.href,
          message,
        });
      }
    }
  };

  const subscriptionForm = (
    <div className="subscription-form">
      <div>
        <div className="input-container">
          <Input
            className="input"
            type="text"
            name="email"
            value={email}
            placeholder="Enter your email"
            onChange={(e) => setEmail(e.target.value)}
          />

          <div className="divider" />

          <Dropdown
            onSelectItem={(_: any, stateIndex: number) =>
              setStateIndex(stateIndex)
            }
            selectedIndex={selectedStateIndex}
            items={STATES}
          />
        </div>
        <div className="error">{error}</div>
      </div>

      <button className="button" onClick={onSubmit}>
        {loading ? <Spinner size="24px" color="#004198" /> : ctaText}
      </button>
    </div>
  );

  const subscriptionSuccess = (
    <div className="subscription-success">
      <div className="icon-container">
        <Icon color="#004198" path={mdiCheckBold} size="30px" />
      </div>
      <div className="success-text-container">
        <p className="success-text">Email Subscribed. Thank you!</p>
        <Link href={`/${DEFAULT_SPORT_KEY}/odds`} legacyBehavior>
          <a className="success-link">
            Go to Today's Free Betting Predictions{" "}
            <Icon color="#004198" path={mdiArrowRight} size="20px" />
          </a>
        </Link>
      </div>
    </div>
  );

  return (
    <div className="email-state-capture">
      <div className="subscription-container">
        <div
          className={`subscription ${isVisible ? "" : "subscription-center"}`}
        >
          <BetQLLogo className="logo" color="white" />

          {isVisible ? subscriptionForm : subscriptionSuccess}
        </div>
      </div>
      <style jsx>{styles}</style>
    </div>
  );
};

export default EmailStateCapture;
